/*
  Банкротство должников на карточке счёта - §3.7, §6.10.

  Зачем это здесь. Пошлина за банкрота уходит впустую, а долг списывается: до
  24.09.2026 система выглядела проверяющей банкротство и не проверяла - признак
  в базе был, а записывать в него было нечему. Теперь сведения вносит человек
  здесь либо приносит перечень файлом (раздел «Загрузка», вкладка «Банкроты»).

  🔴 Процедура принадлежит ЧЕЛОВЕКУ, а не счёту, и блок это показывает прямо:
  один банкрот из четырёх сособственников счёт не закрывает - заявление к
  остальным подаётся обычным порядком, уменьшаясь на его долю. Без списка имён
  снятые требования выглядели бы пропажей.

  Проверки дат делает сервер и только он: те же, что у загрузки файлом, и вторая
  их копия на клиенте разошлась бы с первой молча.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import BankruptcyRemove from './BankruptcyRemove.jsx'
import { Field, Notice } from './Common.jsx'
import PageBar from './PageBar.jsx'
import Select from './Select.jsx'
import usePaging from './usePaging.js'

const KINDS = { judicial: 'Судебное', extrajudicial: 'Внесудебное (через МФЦ)' }
const STATES = { introduced: 'Идёт', completed: 'Завершена, долг списан' }

const EMPTY = {
  person_id: '',
  kind: 'judicial',
  state: 'introduced',
  opened_on: '',
  case_number: '',
  claims_deadline: '',
  completed_on: '',
}

/* Дата с сервера приходит машинным видом «2026-03-16»; человек читает даты так,
   как они написаны в определении суда. Формат один на кабинет (§4.5.1). */
function shownDate(value) {
  if (!value) return null
  const [year, month, day] = String(value).split('-')
  return `${day}.${month}.${year}`
}

/* Срок до закрытия реестра требований: он не возобновляется, и пропускают его
   молча. Отрицательное число - не «нет срока», а «уже прошёл»: требование за
   реестром удовлетворяется после всех остальных. */
function deadlineNote(procedure) {
  if (procedure.claims_deadline === null) return null
  const left = procedure.days_until_deadline
  const date = shownDate(procedure.claims_deadline)
  if (left === null) return `Реестр требований закрылся ${date}.`
  if (left < 0) return `Реестр требований закрылся ${date}: срок прошёл ${-left} дн. назад.`
  return `Реестр требований закрывается ${date}: осталось ${left} дн.`
}

/* Пустое поле не отправляется вовсе: сервер отличает «не указано» от значения,
   а пустая строка вместо даты - это ошибка разбора, а не отсутствие сведений. */
function payloadOf(values) {
  const payload = { kind: values.kind, state: values.state, opened_on: values.opened_on }
  ;['case_number', 'claims_deadline', 'completed_on'].forEach((key) => {
    const value = String(values[key]).trim()
    if (value !== '') payload[key] = value
  })
  return payload
}

export default function AccountBankruptcy({ card, canEdit, onDone }) {
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState(EMPTY)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const found = card.bankruptcies ?? []
  // Процедур по счёту обычно одна-две, но у счёта с десятком собственников их больше;
  // с 21.10.2026 - общая панель страниц (покажет лишь «Всего процедур: N», пока их мало).
  const { rows: pageFound, bar } = usePaging(found)
  const debtors = Object.fromEntries(card.debtors.map((d) => [d.person_id, d.full_name]))

  const start = () => {
    setValues({ ...EMPTY, person_id: card.debtors.length === 1 ? card.debtors[0].person_id : '' })
    setError(null)
    setEditing(true)
  }

  const save = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await api.recordBankruptcy(values.person_id, payloadOf(values))
      setEditing(false)
      onDone(
        result.blocked_accounts > 0
          ? `Сведения внесены. Подача остановлена по счетам: ${result.blocked_accounts}.`
          : 'Сведения внесены. Счёт остаётся в работе: по нему есть кому платить.',
      )
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="section card">
      <h2 className="section__title">Банкротство</h2>

      {found.length === 0 ? (
        <div className="muted">
          Процедур банкротства по должникам этого счёта не найдено. Сведения вносятся здесь
          или перечнем в разделе «Загрузка»: Арбитр ЖКХ не узнаёт о банкротстве сам. Спросить
          реестр можно кнопкой ниже, но реестр у службы пока тестовый, и его «не найден» не
          значит «не банкрот».
        </div>
      ) : (
        /*
          Не таблицей, а строками: в паре «объяснение - кнопка» первая ячейка
          занимает всю ширину карточки, и кнопка в числовой колонке уезжала за
          её правый край (найдено осмотром 24.09.2026). Кнопка стоит под
          текстом, как в соседних блоках карточки.
        */
        pageFound.map((procedure) => (
          <div key={procedure.id}>
            {/* Имени отдельной строкой нет: объяснение с сервера начинается с
                него же, и две одинаковые фамилии подряд читались бы как две
                разные записи. */}
            <div>{procedure.explanation}</div>
            {deadlineNote(procedure) ? (
              <div className="muted">{deadlineNote(procedure)}</div>
            ) : null}
            {canEdit ? (
              <div className="row row--tight">
                <BankruptcyRemove procedure={procedure} onDone={onDone} />
              </div>
            ) : null}
          </div>
        ))
      )}
      <PageBar {...bar} what="процедур" />

      {editing ? (
        <form onSubmit={save}>
          <div className="row row--fields">
            <div className="field field--m">
              <span className="field__label" id="bankruptcy-person-label">
                Должник
              </span>
              <Select
                value={values.person_id}
                onChange={(value) => setValues({ ...values, person_id: value })}
                options={debtors}
                empty="выберите должника"
                disabled={busy}
                labelledBy="bankruptcy-person-label"
              />
            </div>

            <div className="field field--m">
              <span className="field__label" id="bankruptcy-kind-label">
                Вид процедуры
              </span>
              <Select
                value={values.kind}
                onChange={(value) => setValues({ ...values, kind: value })}
                options={KINDS}
                disabled={busy}
                labelledBy="bankruptcy-kind-label"
              />
            </div>

            <div className="field field--m">
              <span className="field__label" id="bankruptcy-state-label">
                Состояние
              </span>
              <Select
                value={values.state}
                onChange={(value) => setValues({ ...values, state: value })}
                options={STATES}
                disabled={busy}
                labelledBy="bankruptcy-state-label"
              />
            </div>
          </div>

          <div className="row row--fields">
            <Field label="Дата возбуждения" width="s">
              <input
                className="field__input"
                type="date"
                value={values.opened_on}
                disabled={busy}
                onChange={(event) => setValues({ ...values, opened_on: event.target.value })}
              />
            </Field>

            <Field label="Закрытие реестра требований" width="s">
              <input
                className="field__input"
                type="date"
                value={values.claims_deadline}
                disabled={busy}
                onChange={(event) => setValues({ ...values, claims_deadline: event.target.value })}
              />
            </Field>

            {values.state === 'completed' ? (
              <Field label="Дата завершения" width="s">
                <input
                  className="field__input"
                  type="date"
                  value={values.completed_on}
                  disabled={busy}
                  onChange={(event) => setValues({ ...values, completed_on: event.target.value })}
                />
              </Field>
            ) : null}

            <Field label="Номер дела" width="m">
              <input
                className="field__input"
                type="text"
                value={values.case_number}
                placeholder="А40-12345/2026"
                disabled={busy}
                onChange={(event) => setValues({ ...values, case_number: event.target.value })}
              />
            </Field>
          </div>

          <div className="muted">
            Дата возбуждения обязательна: от неё считается, какая часть долга относится к
            процедуре. Пока процедура идёт, заявление по этому должнику не формируется, а
            требование заявляется в деле о банкротстве.
          </div>

          <Notice error={error} />

          <div className="row row--tight">
            <button className="button" type="submit" disabled={busy || !values.person_id}>
              {busy ? 'Сохраняем…' : 'Внести'}
            </button>
            <button
              className="button button--quiet"
              type="button"
              disabled={busy}
              onClick={() => setEditing(false)}
            >
              Отмена
            </button>
          </div>
        </form>
      ) : (
        <>
          <Notice error={error} />
          {/* Кнопки нет вовсе у того, кому это не положено: запись останавливает
              подачу заявлений, и право у неё то же, что на сборку пакета. */}
          {canEdit && card.debtors.length > 0 ? (
            <div className="row row--tight">
              <button type="button" className="button button--quiet" onClick={start}>
                Внести банкротство
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  )
}
